import { useCallback, useMemo } from 'react'
import type { RubroRow, SkillOption } from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { rubroHasValidSelectedSkill } from '../../../../../../../utils/conversationalMode/projectAutofillDraftUtils.ts'
import { findRubroRowIndex, rubroHasDocumentationToReset } from '../projectAutofillCardUtils.ts'
import { computeRecommendedQuotationWindow } from '../../rubroQuotationTimelineUtils.ts'

type SaveState = 'idle' | 'ok' | 'error'

export function useProjectAutofillRubroInteractions(args: {
  rubros: RubroRow[]
  draftLocked: boolean
  startDate: string
  endDate: string
  skillOptions: SkillOption[]
  isCreateObraShell: boolean
  createObraRubroModalRid: string | null
  budgetSheetFullscreenTempId: string | null
  metricSheetFullscreenTempId: string | null
  technicalSpecFullscreenTempId: string | null
  setRubros: React.Dispatch<React.SetStateAction<RubroRow[]>>
  setExpandedRubros: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  setFuzzyConfirmByRubro: React.Dispatch<React.SetStateAction<Record<string, boolean>>>
  setSaveState: React.Dispatch<React.SetStateAction<SaveState>>
  setCreateObraRubroModalRid: React.Dispatch<React.SetStateAction<string | null>>
  setBudgetSheetFullscreenTempId: React.Dispatch<React.SetStateAction<string | null>>
  setMetricSheetFullscreenTempId: React.Dispatch<React.SetStateAction<string | null>>
  setTechnicalSpecFullscreenTempId: React.Dispatch<React.SetStateAction<string | null>>
  setPendingRubroSkillChange: React.Dispatch<
    React.SetStateAction<{ tempId: string; skill: SkillOption } | null>
  >
}) {
  const {
    rubros,
    draftLocked,
    startDate,
    endDate,
    skillOptions,
    isCreateObraShell,
    createObraRubroModalRid,
    budgetSheetFullscreenTempId,
    metricSheetFullscreenTempId,
    technicalSpecFullscreenTempId,
    setRubros,
    setExpandedRubros,
    setFuzzyConfirmByRubro,
    setSaveState,
    setCreateObraRubroModalRid,
    setBudgetSheetFullscreenTempId,
    setMetricSheetFullscreenTempId,
    setTechnicalSpecFullscreenTempId,
    setPendingRubroSkillChange
  } = args

  const addRubroBlockedByIncompleteManual = useMemo(
    () => rubros.some((r) => r.manualAdd === true && !rubroHasValidSelectedSkill(r)),
    [rubros]
  )

  const updateRubro = (tempId: string, updates: Partial<Omit<RubroRow, 'tempId'>>) => {
    setRubros((prev) => {
      const idx = findRubroRowIndex(prev, tempId)
      if (idx < 0) return prev
      const next = [...prev]
      next[idx] = { ...next[idx], ...updates }
      return next
    })
    setSaveState('idle')
  }

  const applyRubroSkillChange = useCallback(
    (tempId: string, selectedSkill: SkillOption, clearDocumentation: boolean) => {
      setRubros((prev) => {
        const idx = findRubroRowIndex(prev, tempId)
        if (idx < 0) return prev
        const nextRows = [...prev]
        const current = nextRows[idx]
        const next: RubroRow = {
          ...current,
          detectedLabel: selectedSkill.name,
          selectedSkillId: selectedSkill.id,
          matchedSkillName: selectedSkill.name,
          skillMatchKind: undefined
        }
        if (clearDocumentation) {
          next.documentationSlots = {}
          next.sourcePages = undefined
          next.documentationFileIds = undefined
        }
        nextRows[idx] = next
        return nextRows
      })
      setFuzzyConfirmByRubro((prev) => ({ ...prev, [tempId]: false }))
      setSaveState('idle')
    },
    [setRubros, setFuzzyConfirmByRubro, setSaveState]
  )

  const handleRubroSkillSelect = (tempId: string, selectedValue: string, currentRow: RubroRow) => {
    if (draftLocked) return
    if (!selectedValue) {
      updateRubro(tempId, {
        selectedSkillId: undefined,
        detectedLabel: '',
        matchedSkillName: undefined,
        skillMatchKind: undefined
      })
      setFuzzyConfirmByRubro((prev) => ({ ...prev, [tempId]: false }))
      return
    }
    const skillId = Number(selectedValue)
    const selectedSkill = skillOptions.find((skill) => skill.id === skillId)
    if (!selectedSkill) return
    if (currentRow.selectedSkillId === selectedSkill.id) return
    if (rubroHasDocumentationToReset(currentRow)) {
      setPendingRubroSkillChange({ tempId, skill: selectedSkill })
      return
    }
    applyRubroSkillChange(tempId, selectedSkill, false)
  }

  const handleAddRubro = useCallback(() => {
    if (draftLocked) return
    if (addRubroBlockedByIncompleteManual) return
    const tid = `rubro-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`
    const recommended =
      computeRecommendedQuotationWindow(startDate, endDate) ??
      (startDate && endDate ? { startDate, endDate } : null)
    setRubros((prev) => [
      ...prev,
      {
        tempId: tid,
        detectedLabel: '',
        description: '',
        status: 'OPEN',
        datesInheritedFromProject: false,
        startDate: recommended?.startDate || startDate || '',
        endDate: recommended?.endDate || endDate || '',
        recommendedQuotationWindow: recommended || undefined,
        manualAdd: true
      }
    ])
    setExpandedRubros((prev) => ({ ...prev, [tid]: true }))
    setFuzzyConfirmByRubro((prev) => ({ ...prev, [tid]: false }))
    setSaveState('idle')
    if (isCreateObraShell) {
      setCreateObraRubroModalRid(tid)
    }
  }, [
    draftLocked,
    addRubroBlockedByIncompleteManual,
    startDate,
    endDate,
    isCreateObraShell,
    setRubros,
    setExpandedRubros,
    setFuzzyConfirmByRubro,
    setSaveState,
    setCreateObraRubroModalRid
  ])

  const handleRemoveRubro = useCallback(
    (tempId: string) => {
      if (draftLocked) return
      setRubros((prev) => {
        const idx = findRubroRowIndex(prev, tempId)
        if (idx < 0) return prev
        const next = [...prev]
        next.splice(idx, 1)
        return next
      })
      setExpandedRubros((prev) => {
        const next = { ...prev }
        delete next[tempId]
        return next
      })
      if (createObraRubroModalRid === tempId) {
        setCreateObraRubroModalRid(null)
      }
      if (budgetSheetFullscreenTempId === tempId) {
        setBudgetSheetFullscreenTempId(null)
      }
      if (metricSheetFullscreenTempId === tempId) {
        setMetricSheetFullscreenTempId(null)
      }
      if (technicalSpecFullscreenTempId === tempId) {
        setTechnicalSpecFullscreenTempId(null)
      }
      setSaveState('idle')
    },
    [
      draftLocked,
      createObraRubroModalRid,
      budgetSheetFullscreenTempId,
      metricSheetFullscreenTempId,
      technicalSpecFullscreenTempId,
      setRubros,
      setExpandedRubros,
      setCreateObraRubroModalRid,
      setBudgetSheetFullscreenTempId,
      setMetricSheetFullscreenTempId,
      setTechnicalSpecFullscreenTempId,
      setSaveState
    ]
  )

  const toggleRubroExpanded = useCallback(
    (tempId: string) => {
      setExpandedRubros((prev) => ({ ...prev, [tempId]: !prev[tempId] }))
    },
    [setExpandedRubros]
  )

  return {
    updateRubro,
    applyRubroSkillChange,
    handleRubroSkillSelect,
    handleAddRubro,
    handleRemoveRubro,
    toggleRubroExpanded,
    addRubroBlockedByIncompleteManual
  }
}
